/**
 * KioskDocumentViewer — renders the CURRENT capture step's PDF above the
 * signature capture area (Phase A, Bill 2026-07-07).
 *
 * GET /v3/signing/kiosk/document?token= returns raw application/pdf bytes for
 * the current capture step. Re-fetches when `documentId` changes; stale blob
 * URLs are revoked and the parsed document destroyed on change/unmount.
 *
 * RENDERED BY US, NOT BY THE BROWSER (BACKLOG-1539). Bill, as the SIGNER on the
 * 2026-08-03 smoke test: "everything appeared perfectly except the displayed
 * disclosure was missing — a not-available icon appeared each time where the
 * rendered disclosure should appear." The bytes were fine (200) and CSP allowed
 * the frame; the old <iframe> simply depended on the BROWSER'S built-in PDF
 * viewer, which bank group policy commonly disables (AlwaysOpenPdfExternally).
 * Portal 0.11.6 detected that and explained it — but the customer still could
 * not read what they were about to sign. Now every signer gets the same path:
 * pdf.js rasterizes each page onto a <canvas> (bundled worker, no CDN — see
 * services/pdfjs.ts), stacked in one scrollable column, fitted to the container
 * width and drawn at devicePixelRatio so text stays crisp. Browser PDF policy is
 * no longer part of the picture.
 *
 * SURFACES failures instead of hiding them (Bill 2026-07-20): the endpoint fails
 * closed two ways —
 *   NO_DOCUMENT (404)               — no document resolved for this step, and
 *   DOCUMENT_NOT_FLATTENABLE (500)  — the document could not be prepared and MUST
 *                                     NOT be signed.
 * Both used to render as a silent placeholder that even implied the document was
 * fine ("review the document your banker is showing you"). That hid a real
 * "do not continue" warning. Now the actual server message + code is shown so the
 * room knows to stop, and so the exact failure is diagnosable on the spot.
 * Same rule for the renderer: if pdf.js itself cannot load or parse the file we
 * say so (with the reason) and offer the open-in-a-new-tab escape hatch kept
 * from 0.11.4–0.11.6 — never a blank area.
 *
 * CSP note: the pdf.js worker is a same-origin bundled asset, allowed by
 * `script-src 'self'` (worker-src falls back to script-src), so no CSP change
 * was needed. `frame-src blob:` — added for the old iframe and still listed in
 * index.html / vite.config.ts / staticwebapp.config.json — is no longer used by
 * this component; it can be dropped whenever the CSP is next tightened.
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { ENV } from '@/constants/env';
import { kioskFetch } from './kioskSession';
import { loadPdfJs, type PdfDocumentProxy, type PdfPageProxy } from '@/services/pdfjs';

export interface KioskDocumentViewerProps {
  token: string;
  /** Current capture step's document — re-fetch when this changes. */
  documentId: string;
  docTitle?: string | null;
  /** Desktop left-rail layout owns the document label — hide the inline title. */
  showTitle?: boolean;
  /**
   * Scroll depth 0-100 of the rendered pages (review steps, Bill 2026-09-30):
   * called on every scroll and once after the pages render (a document that
   * fits without scrolling reports 100). The caller keeps the monotonic max.
   */
  onScrollProgress?: (pct: number) => void;
}

interface DocError {
  code: string | null;
  message: string;
  /** true = a hard "do not sign" condition (flatten failure); render red. */
  hard: boolean;
}

/** Cap the backing-store scale: 2x is retina-crisp without quadrupling memory. */
const MAX_DPR = 2;
/** Horizontal breathing room around a page inside the scroller (CSS px). */
const PAGE_GUTTER = 16;

const wrapS: CSSProperties = {
  flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
};
const titleS: CSSProperties = {
  margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#1a406a', flex: '0 0 auto',
};
// The scroll container replaces the old <iframe>: same box, same border, but we
// own what is inside it.
const scrollerS: CSSProperties = {
  display: 'block', flex: 1, minHeight: 0, width: '100%', border: '1px solid #d7e0ec',
  borderRadius: 12, background: '#eef3f9', overflowY: 'auto', overflowX: 'hidden',
  padding: `${PAGE_GUTTER}px ${PAGE_GUTTER}px 24px`, WebkitOverflowScrolling: 'touch',
};
const pageWrapS: CSSProperties = {
  margin: '0 auto 18px', width: 'fit-content', maxWidth: '100%',
};
const canvasS: CSSProperties = {
  display: 'block', background: '#fff', borderRadius: 4,
  boxShadow: '0 2px 10px rgba(15,23,42,0.16)', maxWidth: '100%',
};
const pageLabelS: CSSProperties = {
  margin: '6px 0 0', fontSize: 12, color: '#64748b', textAlign: 'center',
};
const pageFailS: CSSProperties = {
  margin: '6px 0 0', fontSize: 14, fontWeight: 700, color: '#b10505', textAlign: 'center',
};
// Visually hidden, still read by screen readers — the canvas itself carries no
// text, so each page exports its extracted text here (the native viewer used to
// provide this).
const srOnlyS: CSSProperties = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};
const loadingS: CSSProperties = {
  flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
  border: '1px dashed #cbd5e1', borderRadius: 12, background: '#fff', color: '#64748b',
  fontSize: 15, lineHeight: 1.5, textAlign: 'center', padding: 24,
};
const errorWrapS = (hard: boolean): CSSProperties => ({
  flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center', gap: 10,
  border: `2px solid ${hard ? '#b10505' : '#c89000'}`, borderRadius: 12,
  background: hard ? '#f9e9e9' : '#fef7e0', padding: 28, textAlign: 'center',
});
const errorTitleS = (hard: boolean): CSSProperties => ({
  margin: 0, fontSize: 18, fontWeight: 700, color: hard ? '#b10505' : '#8a6d00',
});
const errorMsgS: CSSProperties = { margin: 0, fontSize: 15, lineHeight: 1.5, color: '#333', maxWidth: 460 };
const errorCodeS: CSSProperties = { marginTop: 4, fontSize: 12, color: '#8a94a6', fontFamily: 'monospace' };
const openLinkS: CSSProperties = { fontSize: 16, fontWeight: 700, color: '#2968ad', textDecoration: 'underline' };

// True when the browser will NOT render a PDF inline (policy-disabled built-in
// viewer, e.g. AlwaysOpenPdfExternally). navigator.pdfViewerEnabled is the
// standard signal; when it is absent we assume the viewer works, so nothing
// changes for browsers that do not report it. pdf.js no longer depends on this —
// it only decides how we word the fallback if pdf.js ITSELF fails.
function nativePdfViewerBlocked(): boolean {
  try {
    const n = navigator as Navigator & { pdfViewerEnabled?: boolean };
    return typeof n.pdfViewerEnabled === 'boolean' ? !n.pdfViewerEnabled : false;
  } catch {
    return false;
  }
}

/** pdf.js rejects in-flight renders with this when we cancel one — not a failure. */
function isRenderCancellation(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name ?? '';
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return name === 'RenderingCancelledException' || /cancelled/i.test(msg);
}

interface PdfPageCanvasProps {
  page: PdfPageProxy;
  pageNumber: number;
  pageCount: number;
  /** Width to draw the page at, in CSS px (0 = container not measured yet). */
  cssWidth: number;
}

/** One page → one canvas. Re-renders when the available width changes. */
function PdfPageCanvas({ page, pageNumber, pageCount, cssWidth }: PdfPageCanvasProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [failed, setFailed] = useState(false);
  const [text, setText] = useState('');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || cssWidth <= 0) return;
    let cancelled = false;

    // Draw at cssWidth * devicePixelRatio device pixels, then scale the element
    // back down with CSS — that is what keeps small print sharp on hi-DPI kiosk
    // screens instead of a blurry upscale.
    const dpr = Math.min(Math.max(window.devicePixelRatio || 1, 1), MAX_DPR);
    const unscaled = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: (cssWidth / unscaled.width) * dpr });
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.style.width = `${Math.floor(cssWidth)}px`;
    canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;

    const task = page.render({ canvas, viewport });
    task.promise.then(
      () => { if (!cancelled) setFailed(false); },
      (err: unknown) => {
        // A cancellation is us re-rendering at a new width, not a real failure.
        if (cancelled || isRenderCancellation(err)) return;
        setFailed(true);
        // eslint-disable-next-line no-console
        console.warn('[KioskDocumentViewer] page render failed', pageNumber, err);
      }
    );
    return () => {
      cancelled = true;
      task.cancel();
    };
  }, [page, pageNumber, cssWidth]);

  // Screen-reader text for this page (silently skipped if extraction fails —
  // the visible canvas is still there).
  useEffect(() => {
    let cancelled = false;
    void page.getTextContent().then(
      (content) => {
        if (cancelled) return;
        setText(
          content.items
            .map((i) => ('str' in i ? i.str : ''))
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim()
        );
      },
      () => { /* no text layer — canvas still renders */ }
    );
    return () => { cancelled = true; };
  }, [page]);

  return (
    <div style={pageWrapS}>
      {/* The canvas is decorative to assistive tech; the text below carries the content. */}
      <canvas ref={canvasRef} style={canvasS} aria-hidden="true" />
      <span style={srOnlyS}>{`Page ${pageNumber} of ${pageCount}. ${text}`}</span>
      {failed ? (
        <p style={pageFailS} role="alert">
          Page {pageNumber} could not be displayed — please tell your banker.
        </p>
      ) : (
        pageCount > 1 && (
          <p style={pageLabelS} aria-hidden="true">
            Page {pageNumber} of {pageCount}
          </p>
        )
      )}
    </div>
  );
}

export function KioskDocumentViewer(props: KioskDocumentViewerProps): JSX.Element | null {
  const { token, documentId, docTitle, showTitle = true, onScrollProgress } = props;
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const nativePdfBlocked = nativePdfViewerBlocked();
  const [error, setError] = useState<DocError | null>(null);
  const [pages, setPages] = useState<PdfPageProxy[] | null>(null);
  /** Set when pdf.js could not load/parse — triggers the open-in-a-new-tab fallback. */
  const [renderFailure, setRenderFailure] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [pageWidth, setPageWidth] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    let pdfDoc: PdfDocumentProxy | null = null;
    setPdfUrl(null);
    setError(null);
    setPages(null);
    setRenderFailure(null);
    void (async () => {
      try {
        const r = await kioskFetch(
          `${ENV.apiBaseUrl}/v3/signing/kiosk/document?token=${encodeURIComponent(token)}`
        );
        if (cancelled) return;
        if (!r.ok) {
          // Surface the endpoint's real failure (NO_DOCUMENT / DOCUMENT_NOT_FLATTENABLE)
          // rather than a silent placeholder.
          let code: string | null = null;
          let message = '';
          try {
            const body = await r.json();
            code = (body?.errorCode as string) ?? null;
            message = (body?.error as string) ?? '';
          } catch {
            /* non-JSON error body */
          }
          if (cancelled) return;
          const hard = code === 'DOCUMENT_NOT_FLATTENABLE';
          setError({
            code,
            hard,
            message:
              message ||
              (code === 'NO_DOCUMENT'
                ? 'This document is not ready to view yet. Please tell your banker.'
                : `The document could not be loaded (HTTP ${r.status}). Please tell your banker.`),
          });
          return;
        }
        const blob = await r.blob();
        if (cancelled) return;
        // Keep a PDF-typed blob URL: it is what the fallback "open in a new tab"
        // link needs if pdf.js cannot render the file.
        const pdfBlob =
          blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
        objectUrl = URL.createObjectURL(pdfBlob);
        setPdfUrl(objectUrl);

        // ── primary path: rasterize with the bundled pdf.js ──────────────────
        try {
          const bytes = new Uint8Array(await pdfBlob.arrayBuffer());
          if (cancelled) return;
          const pdfjs = await loadPdfJs();
          if (cancelled) return;
          pdfDoc = await pdfjs.getDocument({ data: bytes }).promise;
          if (cancelled) return;
          // A 0-page document is a failure, not a slow load — without this it
          // would sit on "Loading the document…" forever.
          if (pdfDoc.numPages < 1) throw new Error('The document contains no pages.');
          const loaded = await Promise.all(
            Array.from({ length: pdfDoc.numPages }, (_, i) => (pdfDoc as PdfDocumentProxy).getPage(i + 1))
          );
          if (cancelled) return;
          setPages(loaded);
        } catch (e) {
          if (cancelled) return;
          // Loud, not blank: say the renderer failed and why, and keep the
          // open-in-a-new-tab escape hatch (portal 0.11.4–0.11.6 behaviour).
          setRenderFailure(e instanceof Error ? e.message : String(e));
          // eslint-disable-next-line no-console
          console.error('[KioskDocumentViewer] pdf.js could not render the document', e);
        }
      } catch {
        if (!cancelled)
          setError({
            code: 'NETWORK',
            hard: false,
            message:
              'The document could not be loaded — the connection to the bank failed. Please tell your banker.',
          });
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      // Free the worker-side document; pending getPage()s reject harmlessly.
      void pdfDoc?.destroy().catch(() => { /* already torn down */ });
    };
  }, [token, documentId]);

  // Fit pages to the container and re-render them when it resizes (the kiosk
  // flips between the desktop left-rail layout and the stacked mobile one).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = (): void => {
      const w = el.clientWidth - PAGE_GUTTER * 2;
      setPageWidth((prev) => (Math.abs(prev - w) > 1 ? Math.max(0, w) : prev));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [pages]);

  const showPages = !error && !renderFailure && pages && pages.length > 0;

  // Scroll evidence for review steps. Measured on the scroller itself, so it
  // reflects the rendered pages, not the browser window.
  const reportScroll = useCallback(() => {
    if (!onScrollProgress) return;
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    onScrollProgress(max <= 0 ? 100 : Math.min(100, Math.round((el.scrollTop / max) * 100)));
  }, [onScrollProgress]);
  useEffect(() => {
    if (showPages) requestAnimationFrame(reportScroll);
  }, [showPages, pageWidth, reportScroll]);

  return (
    <div style={wrapS}>
      {showTitle && <h2 style={titleS}>{docTitle ?? 'Please review this document'}</h2>}
      {error ? (
        <div style={errorWrapS(error.hard)} role="alert">
          <p style={errorTitleS(error.hard)}>
            {error.hard ? 'Do not sign — please stop' : 'Document not available'}
          </p>
          <p style={errorMsgS}>{error.message}</p>
          {error.code && <p style={errorCodeS}>ref: {error.code}</p>}
        </div>
      ) : pdfUrl && renderFailure ? (
        // ── pdf.js could not render this file (BACKLOG-1539 fallback) ─────────
        // Only reachable when the bundled renderer fails — never for a browser
        // that merely has its own PDF viewer switched off. Explain it and give
        // the signer a way to read the document before signing.
        <div style={errorWrapS(false)} role="alert">
          <p style={errorTitleS(false)}>This screen can&apos;t display the document</p>
          <p style={errorMsgS}>
            The document downloaded correctly, but it could not be displayed on this
            screen.{' '}
            {nativePdfBlocked
              ? 'This browser is also set not to show PDFs in the page, so please ask your banker to display it for you before you sign.'
              : 'Open it in a new tab to read it before you sign — or ask your banker to display it for you.'}
          </p>
          <p style={{ marginTop: 12 }}>
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={openLinkS}>
              Open the document in a new tab
            </a>
          </p>
          <p style={errorCodeS}>
            ref: PDF_RENDER_FAILED{nativePdfBlocked ? ' · PDF_VIEWER_DISABLED' : ''} — {renderFailure}
          </p>
        </div>
      ) : showPages ? (
        // key forces a fresh scroller per document so each one opens at the TOP
        // (Bill 2026-07-13 Item 27 — the old iframe carried scroll position
        // across blob navigations; a remount is still the simplest guarantee).
        <div
          key={documentId}
          ref={scrollRef}
          style={scrollerS}
          role="region"
          aria-label={docTitle ?? 'Document to sign'}
          tabIndex={0}
          onScroll={reportScroll}
        >
          {pages.map((p, i) => (
            <PdfPageCanvas
              key={`${documentId}-${i + 1}`}
              page={p}
              pageNumber={i + 1}
              pageCount={pages.length}
              cssWidth={pageWidth}
            />
          ))}
        </div>
      ) : (
        <div style={loadingS} role="status">
          Loading the document…
        </div>
      )}
    </div>
  );
}

export default KioskDocumentViewer;
